import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { Button, SingleLineText, TypeInput, ValidationTextField } from '@bloomays-lib/ui.shared';

export type SignatureRole = 'bloomer' | 'client';

export type BaseSignatureData = {
  signerName: string;
  signatureDate: string;
  totalDaysWorked: number;
};

export type CRASignatureProps = {
  signatureRole: SignatureRole;
  isSigned: boolean;
  loading?: boolean;
  // Données à afficher quand signé
  signedName?: string;
  signedAt?: string; // ISO string
  // Contraintes de validation (pour le bloomer)
  expectedTotalDays?: number;
  // Callback au clic sur "Signer"
  onSign?: (data: BaseSignatureData) => Promise<void>;
  viewOnly?: boolean;
};

const SignatureBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  minHeight: '200px',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SignatureForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

const SignatureDisplay = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[100],
  borderRadius: theme.shape.borderRadius,
}));

const safeNumbeStringFormat = (value: string | number) => {
  return value.toString().replace('.', ',');
};

const CRASignature: React.FC<CRASignatureProps> = ({
  signatureRole: role,
  isSigned,
  loading = false,
  signedName,
  signedAt,
  expectedTotalDays = 0,
  onSign,
  viewOnly = false,
}) => {
  const [name, setName] = useState<string>('');
  const [totalDaysWorked, setTotalDaysWorked] = useState<string>('0');
  const isBloomer = role === 'bloomer';
  const title = isBloomer ? 'Signature Bloomer' : 'Signature Client';
  const isValid = useMemo(() => {
    const base = name.trim() !== '';
    if (!isBloomer) return base;
    return base && safeNumbeStringFormat(expectedTotalDays || 0) === totalDaysWorked;
  }, [name, isBloomer, expectedTotalDays, totalDaysWorked]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!onSign || !isValid) return;

    const nowIso = new Date().toISOString();
    const payload: BaseSignatureData = {
      signerName: name,
      signatureDate: nowIso,
      totalDaysWorked: expectedTotalDays || 0,
    };
    await onSign(payload);
  };

  return (
    <SignatureBox>
      <SingleLineText variant="heading6" text={title} />
      {isSigned || viewOnly ? (
        <SignatureDisplay>
          {!isSigned ? (
            <SingleLineText variant="body2" text={`Non signé`} />
          ) : (
            <>
              <SingleLineText variant="body2" text={`Signé par: ${signedName || ''}`} />
              {signedAt && (
                <SingleLineText variant="caption" text={`Le ${new Date(signedAt).toLocaleDateString('fr-FR')}`} />
              )}
            </>
          )}
        </SignatureDisplay>
      ) : (
        <SignatureForm onSubmit={handleSubmit} name="cra-signature">
          <ValidationTextField
            type={TypeInput.text}
            label={`Total des jours travaillés à confirmer (${safeNumbeStringFormat(expectedTotalDays || 0)} jours)`}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setTotalDaysWorked(safeNumbeStringFormat(e.target.value))
            }
            placeholder={safeNumbeStringFormat(expectedTotalDays || 0)}
            required
          />
          <ValidationTextField
            type={TypeInput.text}
            label="Prénom Nom"
            value={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            placeholder="Prénom Nom"
            required
          />
          <Button buttonType="submit" textButton="Signer" disable={!isValid || loading} loading={loading} />
        </SignatureForm>
      )}
    </SignatureBox>
  ) as React.ReactNode;
};

export default CRASignature;
